import { pb } from './pb';

/* Who is allowed in.

   RULING (Lucky, 2026-08-10): everyone on the Decked Media team can sign in and edit
   everything, regardless of role. So the gate is membership, not permission — an active
   `core_workspace_members` row on an @deckedmedia.com address. There is deliberately NO role
   tiering anywhere in this app.

   Do not "harden" this by reusing CORE's isEditorRoleServer(): that helper returns true only
   for `admin` and `owner`, which would lock out Alyssa (leadership) and Jason (strategist) —
   the two people the tool is for.

   ⚠️ This is a UX gate, not a security boundary. It runs in the browser and anyone can skip
   it with devtools. Every /api/rof/* Function re-resolves membership server-side from the
   caller's own token. Never delete that server check because this exists. */

export const TEAM_EMAIL_DOMAIN = 'deckedmedia.com';

export interface RosterMember {
  id: string;
  userId: string;
  email: string;
  name: string;
  role: string;
  avatarUrl: string;
}

const escapeFilter = (v: string) => v.replace(/'/g, "\\'");

function normalise(record: Record<string, unknown>): RosterMember {
  // NOTE: the field is `Email` with a CAPITAL E. A lowercase `email` filter returns nothing
  // and reads exactly like "this person is not on the roster". This has burned several
  // sessions across CORE.
  const email = String(record.Email || '').trim();
  return {
    id: String(record.id || ''),
    userId: String(record.user_id || ''),
    email,
    name: String(record.full_name || record.display_name || email.split('@')[0] || 'Team member'),
    role: String(record.role || ''),
    avatarUrl: String(record.avatar_url || ''),
  };
}

/**
 * Find the caller's active roster row. Tries user_id first (the strongest link), then the
 * roster Email, then google_email — which exists so someone signing in with a personal
 * Google account can still be matched to their Decked row.
 */
export async function findActiveMember(userId: string, email: string): Promise<RosterMember | null> {
  const clauses: string[] = [];
  if (userId) clauses.push(`user_id = '${escapeFilter(userId)}'`);
  if (email) {
    clauses.push(`Email = '${escapeFilter(email)}'`);
    clauses.push(`google_email = '${escapeFilter(email)}'`);
  }
  if (clauses.length === 0) return null;

  try {
    const rows = await pb.collection('core_workspace_members').getFullList({
      filter: `status = 'active' && (${clauses.join(' || ')})`,
      // The caller's own row is all we need; a bigger page just moves more data.
      perPage: 5,
    });
    if (!rows.length) return null;
    return normalise(rows[0] as unknown as Record<string, unknown>);
  } catch {
    // A failed lookup is NOT the same as "not a member". Surfacing it as a denial would tell
    // a real teammate they have been removed whenever PocketBase hiccups.
    throw new Error('roster_lookup_failed');
  }
}

export interface GateResult {
  ok: boolean;
  member?: RosterMember;
  /** Shown to the user verbatim, so it must be plain and actionable. */
  reason?: string;
  /** True when the check itself broke, as opposed to the person not being allowed. */
  transient?: boolean;
}

/** Run after ANY successful authentication — password or Google, no exceptions. */
export async function gateCurrentUser(): Promise<GateResult> {
  const model = pb.authStore.record as Record<string, unknown> | null;
  if (!pb.authStore.isValid || !model) return { ok: false, reason: 'Not signed in.' };

  const userId = String(model.id || '');
  const email = String(model.email || '').trim();

  let member: RosterMember | null;
  try {
    member = await findActiveMember(userId, email);
  } catch {
    return {
      ok: false,
      transient: true,
      reason: 'Could not check your access just now. This is usually temporary — try again in a moment.',
    };
  }

  if (!member) {
    return {
      ok: false,
      reason: `${email || 'That account'} is not on the Decked Media team roster. Ask Team to add you.`,
    };
  }

  const domain = member.email.split('@')[1]?.toLowerCase();
  if (domain !== TEAM_EMAIL_DOMAIN) {
    return { ok: false, reason: `Real or Fake is limited to @${TEAM_EMAIL_DOMAIN} accounts.` };
  }

  return { ok: true, member };
}
